import { useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "./button";
import { cn } from "@/lib/utils";

import { OverlayShell } from "./overlay";

type ConfirmationTone = "destructive" | "neutral";

const CONFIRM_VARIANT: Record<ConfirmationTone, "destructive" | "default"> = {
	destructive: "destructive",
	neutral: "default",
};

interface ConfirmationDialogProps {
	slot?: string;
	tone: ConfirmationTone;
	title: ReactNode;
	description?: ReactNode;
	cancelLabel?: ReactNode;
	confirmLabel?: ReactNode;
	pendingLabel: ReactNode;
	isPending: boolean;
	onConfirm: () => void;
	className?: string;
	disabled?: boolean;
	children?: ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	ariaLabel?: string;
	dialogComponent?: string;
	dialogSlot?: string;
	cancelSlot?: string;
	confirmSlot?: string;
}

export function ConfirmationDialog({
	slot,
	tone,
	title,
	description,
	cancelLabel = "Voltar",
	confirmLabel,
	pendingLabel,
	isPending,
	onConfirm,
	className,
	disabled = false,
	children,
	open: openProp,
	onOpenChange,
	ariaLabel,
	dialogComponent = "confirmation-dialog",
	dialogSlot,
	cancelSlot,
	confirmSlot = "confirm",
}: ConfirmationDialogProps) {
	const wasPending = useRef(false);
	const [internalOpen, setInternalOpen] = useState(false);
	const open = openProp ?? internalOpen;
	const setOpen = (value: boolean) => {
		onOpenChange?.(value);

		if (openProp === undefined) {
			setInternalOpen(value);
		}
	};

	useEffect(() => {
		if (wasPending.current && !isPending) {
			setOpen(false);
		}

		wasPending.current = isPending;
	}, [isPending]);

	return (
		<>
			{children !== undefined && (
				<button
					type="button"
					data-slot={slot}
					aria-label={ariaLabel}
					aria-haspopup="dialog"
					disabled={disabled}
					onClick={() => setOpen(true)}
					className={cn("inline-flex min-h-11 items-center justify-center md:min-h-8", className)}
				>
					{children}
				</button>
			)}

			{open && (
				<OverlayShell
					label={typeof title === "string" ? title : "Confirmar ação"}
					onClose={() => {
						if (!isPending) {
							setOpen(false);
						}
					}}
					className="items-center justify-center p-4"
				>
					<div
						data-component={dialogComponent}
						data-slot={dialogSlot}
						data-title={typeof title === "string" ? title : undefined}
						className="overlay-panel max-w-md gap-5 p-5 sm:p-6"
					>
						<div>
							<h2 className="type-section-title text-foreground">{title}</h2>
							{description !== undefined && (
								<p className="mt-1 text-sm text-muted-foreground">{description}</p>
							)}
						</div>

						<div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
							<Button
								type="button"
								variant="secondary"
								size="sm"
								data-slot={cancelSlot}
								disabled={isPending}
								onClick={() => setOpen(false)}
							>
								{cancelLabel}
							</Button>

							<Button
								type="button"
								variant={CONFIRM_VARIANT[tone]}
								size="sm"
								data-slot={confirmSlot}
								disabled={isPending || disabled}
								onClick={onConfirm}
								className="px-4"
							>
								{isPending && <Loader2 className="animate-spin" />}
								{isPending && pendingLabel}
								{!isPending && (confirmLabel ?? children)}
							</Button>
						</div>
					</div>
				</OverlayShell>
			)}
		</>
	);
}
